import React, { useEffect, useState, useCallback } from "react";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import withAuth from "../withAuth";
import axios from "axios";
import { handleAuthError } from "../../utils";
import { useConfirm } from "../../contexts/ConfirmContext";
import PaginationControls from "../../components/Common/PaginationControls";
import {
  Col,
  Container,
  Row,
  Card,
  CardBody,
  Table,
  Button,
  Spinner,
  Input,
} from "reactstrap";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { baseUrl } from "../../Network";

const DuplicateChild = () => {
  document.title = "Duplicate Children | RYD Admin";
  const { showConfirm } = useConfirm();

  const [duplicates, setDuplicates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoadingId, setActionLoadingId] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [deactivatedFilter, setDeactivatedFilter] = useState("all");
  const [registrationFilter, setRegistrationFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [showAll, setShowAll] = useState(false);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 20,
    hasNextPage: false,
    hasPreviousPage: false,
  });

  const fetchDuplicateChildren = useCallback(async () => {
    try {
      setLoading(true);
      const limit = showAll ? 10000 : pageSize;
      const params = {
        page: currentPage,
        limit,
        ...(searchQuery.trim() && { search: searchQuery.trim() }),
        ...(deactivatedFilter !== "all" ? { deactivated: deactivatedFilter } : {}),
        ...(registrationFilter !== "all" ? { registered: registrationFilter } : {}),
      };
      const response = await axios.get(`${baseUrl}/admin/child/duplicates`, { params });
      const body = response.data;
      const payload = body?.data;
      const data = Array.isArray(payload) ? payload : payload?.data || [];
      const paginationData = payload?.pagination || {
        currentPage: 1,
        totalPages: 1,
        totalItems: Array.isArray(data) ? data.length : 0,
        itemsPerPage: limit,
        hasNextPage: false,
        hasPreviousPage: false,
      };
      setDuplicates(Array.isArray(data) ? data : []);
      setPagination(paginationData);
    } catch (error) {
      handleAuthError(error);
      console.error(error);
      toast.error("Failed to fetch duplicate children");
      setDuplicates([]);
    } finally {
      setLoading(false);
    }
  }, [currentPage, pageSize, searchQuery, deactivatedFilter, registrationFilter, showAll]);

  useEffect(() => {
    fetchDuplicateChildren();
  }, [fetchDuplicateChildren]);

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1);
  };

  const handleDeactivatedChange = (e) => {
    setDeactivatedFilter(e.target.value);
    setCurrentPage(1);
  };

  const handleRegistrationChange = (e) => {
    setRegistrationFilter(e.target.value);
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (size) => {
    if (size === "all") {
      setShowAll(true);
      setPageSize(10000);
    } else {
      setShowAll(false);
      setPageSize(parseInt(size, 10));
      setCurrentPage(1);
    }
  };

  const goToNextPage = () => {
    if (pagination.hasNextPage) setCurrentPage((p) => p + 1);
  };

  const goToPreviousPage = () => {
    if (pagination.hasPreviousPage) setCurrentPage((p) => p - 1);
  };

  const toggleChildDeactivation = (row, deactivate) => {
    showConfirm({
      title: deactivate ? "Deactivate child?" : "Activate child?",
      message: deactivate
        ? "The parent will no longer see this child on their dashboard. You can activate the child again later."
        : "The parent will see this child on their dashboard again.",
      variant: deactivate ? "warning" : "primary",
      confirmLabel: deactivate ? "Deactivate" : "Activate",
    }).then(async (ok) => {
      if (!ok) return;
      setActionLoadingId(row.id);
      try {
        await axios.put(`${baseUrl}/admin/child/deactivation/${row.id}`, {
          isDeactivatedByAdmin: !!deactivate,
        });
        toast.success(deactivate ? "Child deactivated" : "Child activated");
        await fetchDuplicateChildren();
      } catch (error) {
        handleAuthError(error);
        toast.error("Failed to update child status");
      } finally {
        setActionLoadingId(null);
      }
    });
  };

  const downloadCSV = async () => {
    try {
      const params = {
        page: 1,
        limit: 10000,
        ...(searchQuery.trim() && { search: searchQuery.trim() }),
        ...(deactivatedFilter !== "all" ? { deactivated: deactivatedFilter } : {}),
        ...(registrationFilter !== "all" ? { registered: registrationFilter } : {}),
      };
      const response = await axios.get(`${baseUrl}/admin/child/duplicates`, { params });
      const payload = response.data?.data;
      const rowsData = Array.isArray(payload) ? payload : payload?.data || [];
      if (!rowsData.length) {
        toast.warning("No data to download for the current filters");
        return;
      }

      const headers = [
        "Child Name",
        "Parent Name",
        "Email Address",
        "Package Title",
        "Level",
        "Registration Status",
        "Deactivated (admin)",
      ];

      const rows = rowsData.map((duplicate) => [
        duplicate.childName || "N/A",
        duplicate.parentName || "N/A",
        duplicate.email || "N/A",
        duplicate.packageTitle || "N/A",
        duplicate.level || "N/A",
        duplicate.registrationStatus || "N/A",
        duplicate.isDeactivatedByAdmin ? "Yes" : "No",
      ]);

      const csvContent = [
        headers.join(","),
        ...rows.map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(",")),
      ].join("\n");

      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute(
        "download",
        `duplicate_children_${new Date().toISOString().split("T")[0]}.csv`
      );
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast.success("CSV file downloaded successfully");
    } catch (error) {
      handleAuthError(error);
      toast.error("Failed to export CSV");
    }
  };

  const rowNumber = (index) =>
    (pagination.currentPage - 1) * pagination.itemsPerPage + index + 1;

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="Duplicate Children" breadcrumbItem="Duplicate Children" />

          <Row>
            <Col xl={12}>
              <Card>
                <CardBody>
                  <div className="d-flex flex-wrap justify-content-between align-items-center gap-3 mb-3">
                    <h4 className="card-title mb-0">
                      Duplicate Children List
                      <span className="text-muted fw-normal ms-2 fs-6">
                        ({pagination.totalItems ?? 0})
                      </span>
                    </h4>
                    <div className="d-flex flex-wrap align-items-center gap-2">
                      <Input
                        type="text"
                        bsSize="sm"
                        placeholder="Search name, email, package…"
                        value={searchQuery}
                        onChange={handleSearchChange}
                        style={{ minWidth: "200px" }}
                      />
                      <Input
                        type="select"
                        bsSize="sm"
                        value={registrationFilter}
                        onChange={handleRegistrationChange}
                        style={{ minWidth: "200px" }}
                      >
                        <option value="all">Registration: All</option>
                        <option value="registered">Registered</option>
                        <option value="not_registered">Not registered</option>
                      </Input>
                      <Input
                        type="select"
                        bsSize="sm"
                        value={deactivatedFilter}
                        onChange={handleDeactivatedChange}
                        style={{ minWidth: "200px" }}
                      >
                        <option value="all">Deactivated: All</option>
                        <option value="yes">Deactivated: Yes</option>
                        <option value="no">Deactivated: No</option>
                      </Input>
                      <Button
                        color="light"
                        size="sm"
                        onClick={() => fetchDuplicateChildren()}
                        disabled={loading}
                      >
                        Refresh
                      </Button>
                      <Button
                        color="primary"
                        size="sm"
                        onClick={downloadCSV}
                        disabled={loading || pagination.totalItems === 0}
                      >
                        <i className="mdi mdi-download me-1"></i> Download CSV
                      </Button>
                    </div>
                  </div>

                  {loading ? (
                    <div className="text-center py-5">
                      <Spinner color="primary" />
                      <p className="mt-2">Loading duplicate children...</p>
                    </div>
                  ) : pagination.totalItems === 0 ? (
                    <div className="text-center py-5">
                      <p className="text-muted">No duplicate children match your filters.</p>
                    </div>
                  ) : (
                    <>
                      <div className="table-responsive">
                        <Table className="table table-striped table-bordered">
                          <thead>
                            <tr>
                              <th>#</th>
                              <th>Child Name</th>
                              <th>Parent Name</th>
                              <th>Email Address</th>
                              <th>Package Title</th>
                              <th>Level</th>
                              <th>Registration Status</th>
                              <th>Deactivated</th>
                              <th>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {duplicates.map((duplicate, index) => (
                              <tr key={duplicate.id || index}>
                                <td>{rowNumber(index)}</td>
                                <td>{duplicate.childName || "N/A"}</td>
                                <td>{duplicate.parentName || "N/A"}</td>
                                <td>{duplicate.email || "N/A"}</td>
                                <td>{duplicate.packageTitle || "N/A"}</td>
                                <td>{duplicate.level || "N/A"}</td>
                                <td>
                                  <span
                                    className={`badge ${
                                      duplicate.registrationStatus === "Registered"
                                        ? "bg-success"
                                        : "bg-warning"
                                    }`}
                                  >
                                    {duplicate.registrationStatus || "N/A"}
                                  </span>
                                </td>
                                <td>
                                  <span
                                    className={`badge ${
                                      duplicate.isDeactivatedByAdmin ? "bg-success" : "bg-danger"
                                    }`}
                                  >
                                    {duplicate.isDeactivatedByAdmin ? "Yes" : "No"}
                                  </span>
                                </td>
                                <td>
                                  <Button
                                    type="button"
                                    color={duplicate.isDeactivatedByAdmin ? "success" : "danger"}
                                    size="sm"
                                    className="d-inline-flex align-items-center gap-2 justify-content-center"
                                    style={{ minWidth: "7.5rem" }}
                                    disabled={actionLoadingId !== null}
                                    onClick={() =>
                                      toggleChildDeactivation(
                                        duplicate,
                                        !duplicate.isDeactivatedByAdmin
                                      )
                                    }
                                  >
                                    {actionLoadingId === duplicate.id ? (
                                      <>
                                        <Spinner size="sm" className="flex-shrink-0" />
                                        <span>Processing…</span>
                                      </>
                                    ) : duplicate.isDeactivatedByAdmin ? (
                                      "Activate"
                                    ) : (
                                      "Deactivate"
                                    )}
                                  </Button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </div>
                      <PaginationControls
                        currentPage={pagination.currentPage}
                        totalPages={pagination.totalPages}
                        totalItems={pagination.totalItems}
                        pageSize={pagination.itemsPerPage}
                        showAll={showAll}
                        handlePageChange={handlePageChange}
                        handlePageSizeChange={handlePageSizeChange}
                        goToNextPage={goToNextPage}
                        goToPreviousPage={goToPreviousPage}
                        canPreviousPage={pagination.hasPreviousPage}
                        canNextPage={pagination.hasNextPage}
                        paginatedDataLength={duplicates.length}
                      />
                    </>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
      <ToastContainer />
    </React.Fragment>
  );
};

export default withAuth(DuplicateChild);
